import { useEffect, useState } from "react";
import { X, ExternalLink, CalendarCheck } from "lucide-react";

// Partner / sponsored banner shown at the top and bottom of every page.
// Destination link provided by the partner (isolated here for easy updates).
const PARTNER_URL =
  "https://www.instagram.com/blue_diamond_ink_tattoo?stkn=MWh6N3l5OTc0M2FlZQ==";
// Instagram direct-message deep link to the studio account.
const PARTNER_DM_URL = "https://ig.me/m/blue_diamond_ink_tattoo";
const DISMISS_KEY = "partner_banner_dismissed_bluediamond";
const DISMISS_EVENT = "partner-banner-dismiss";

export const PartnerBanner = ({ position = "top" }) => {
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    try {
      setHidden(localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setHidden(false);
    }
    const onDismiss = () => setHidden(true);
    window.addEventListener(DISMISS_EVENT, onDismiss);
    return () => window.removeEventListener(DISMISS_EVENT, onDismiss);
  }, []);

  if (hidden) return null;

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch (e) {
      console.debug("[partner-banner] could not persist dismiss:", e);
    }
    setHidden(true);
    window.dispatchEvent(new Event(DISMISS_EVENT));
  };

  const edge = position === "bottom" ? "border-t" : "border-b";

  return (
    <div
      data-testid={`partner-banner-${position}`}
      className={`relative z-[150] w-full ${edge} border-amber-500/40 bg-black`}
    >
      <div className="flex items-center gap-2 px-3 py-1.5 pr-10 sm:px-4 sm:pr-12">
        <a
          href={PARTNER_URL}
          target="_blank"
          rel="noopener noreferrer sponsored"
          data-testid={`partner-banner-link-${position}`}
          className="flex min-w-0 flex-1 items-center gap-3 transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70"
        >
          <img
            src="/partner-blue-diamond.webp"
            alt="Blue Diamond Ink Tattoo"
            className="h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-amber-500/50 sm:h-10 sm:w-10"
            loading="lazy"
            width={40}
            height={40}
          />
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="truncate text-[10px] font-medium uppercase tracking-[0.18em] text-amber-500/80">
              Partenaire
            </span>
            <span className="truncate text-sm font-semibold tracking-wide text-amber-200 sm:text-base">
              Blue Diamond Ink Tattoo
              <span className="ml-2 hidden text-xs font-normal text-amber-100/60 sm:inline">
                Studio de tatouage
              </span>
            </span>
          </div>
        </a>
        <a
          href={PARTNER_DM_URL}
          target="_blank"
          rel="noopener noreferrer sponsored"
          data-testid={`partner-banner-rdv-${position}`}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-black transition-colors hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
        >
          <CalendarCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Prendre RDV
        </a>
        <a
          href={PARTNER_URL}
          target="_blank"
          rel="noopener noreferrer sponsored"
          tabIndex={-1}
          aria-hidden="true"
          className="hidden shrink-0 items-center gap-1.5 rounded-full border border-amber-500/50 px-3 py-1 text-xs font-semibold text-amber-200 transition-colors hover:bg-amber-500/10 md:inline-flex"
        >
          Découvrir
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Fermer la bannière partenaire"
        data-testid={`partner-banner-close-${position}`}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-amber-200/70 transition-colors hover:bg-amber-500/10 hover:text-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};
